import { useState } from 'react';

import BpkButton, {
  BUTTON_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-button';
import BpkSmallChevronDownIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/chevron-down';
import {
  BpkBox,
  BpkSpacing,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import BpkSectionList, {
  BpkSectionListSection,
  BpkSectionListItem,
} from '@skyscanner-internal/backpack-web/bpk-component-section-list';
import BpkText from '@skyscanner-internal/backpack-web/bpk-component-text';

import withErrorBoundary from '../ErrorBoundary';

import type { FlightsToNearByAlternativesProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/FlightsToNearbyAlternativesProps';

import STYLES from './FlightsToNearbyAlternatives.module.scss';

const MIN_ITEMS_TO_TRUNCATE = 6;

const HIDDEN_STATE = {
  sectionClass: `${STYLES.FlightsToNearbyAlternatives__hide} ${STYLES.FlightsToNearbyAlternatives__section}`,
  buttonClass: String(STYLES.FlightsToNearbyAlternatives__button),
};

const NO_SHOW_STATE = {
  sectionClass: String(STYLES.FlightsToNearbyAlternatives__section),
  buttonClass: `${STYLES.FlightsToNearbyAlternatives__button} ${STYLES.FlightsToNearbyAlternatives__buttonNoshow}`,
};

const FlightsToNearbyAlternatives = ({
  mode,
  secondaryTitle,
  secondaryUrlsAndLabels,
  strings,
  subtitle,
  title,
  urlsAndLabels,
}: FlightsToNearByAlternativesProps) => {
  const [style, setStyle] = useState(
    urlsAndLabels.length > MIN_ITEMS_TO_TRUNCATE ? HIDDEN_STATE : NO_SHOW_STATE,
  );
  const [secondaryStyle, setSecondaryStyle] = useState(
    secondaryUrlsAndLabels &&
      secondaryUrlsAndLabels.length > MIN_ITEMS_TO_TRUNCATE
      ? HIDDEN_STATE
      : NO_SHOW_STATE,
  );

  const isUnrestrictedHeight = mode === 'country' || mode === 'country-from';

  const getShowAllState = () => ({
    sectionClass: isUnrestrictedHeight
      ? String(STYLES.FlightsToNearbyAlternatives__section__fullHeight)
      : String(STYLES.FlightsToNearbyAlternatives__section),
    buttonClass: isUnrestrictedHeight
      ? `${STYLES.FlightsToNearbyAlternatives__button_fullHeight} ${STYLES.FlightsToNearbyAlternatives__buttonHidden}`
      : `${STYLES.FlightsToNearbyAlternatives__button} ${STYLES.FlightsToNearbyAlternatives__buttonHidden}`,
  });

  const showAll = () => {
    setStyle(getShowAllState());
  };

  const showAllSecondary = () => {
    setSecondaryStyle(getShowAllState());
  };

  return (
    <BpkBox>
      {urlsAndLabels && urlsAndLabels.length > 0 && (
        <BpkSectionList>
          <BpkBox marginTop={BpkSpacing.Base} marginBottom={BpkSpacing.Base}>
            <BpkText tagName="h2" textStyle="heading-2">
              {title}
            </BpkText>
          </BpkBox>
          {subtitle && (
            <BpkBox marginTop={BpkSpacing.Base} marginBottom={BpkSpacing.Base}>
              <BpkText tagName="h3" textStyle="heading-3">
                {subtitle}
              </BpkText>
            </BpkBox>
          )}
          <BpkSectionListSection className={style.sectionClass}>
            {urlsAndLabels.map((link) => (
              <BpkSectionListItem
                className={STYLES.FlightsToNearbyAlternatives__ulist}
                key={link.url}
                href={link.url}
                rel="nofollow"
                secondaryLabel={link.secondaryLabel}
              >
                {link.label}
              </BpkSectionListItem>
            ))}
            <BpkButton
              className={style.buttonClass}
              onClick={() => showAll()}
              type={BUTTON_TYPES.secondary}
            >
              {strings['component_flights-to-nearby-alternatives_show_all']}
              <BpkSmallChevronDownIcon
                className={STYLES.FlightsToNearbyAlternatives__chevronDown}
              />
            </BpkButton>
          </BpkSectionListSection>
        </BpkSectionList>
      )}
      {secondaryUrlsAndLabels && secondaryUrlsAndLabels.length > 0 && (
        <BpkSectionList>
          <BpkBox marginTop={BpkSpacing.Base} marginBottom={BpkSpacing.Base}>
            <BpkText tagName="h2" textStyle="heading-2">
              {secondaryTitle}
            </BpkText>
          </BpkBox>
          <BpkSectionListSection className={secondaryStyle.sectionClass}>
            {secondaryUrlsAndLabels.map((link) => (
              <BpkSectionListItem
                className={STYLES.FlightsToNearbyAlternatives__ulist}
                key={link.url}
                href={link.url}
                rel="nofollow"
                secondaryLabel={link.secondaryLabel}
              >
                {link.label}
              </BpkSectionListItem>
            ))}
            <BpkButton
              className={secondaryStyle.buttonClass}
              onClick={() => showAllSecondary()}
              type={BUTTON_TYPES.secondary}
            >
              {strings['component_flights-to-nearby-alternatives_show_all']}
              <BpkSmallChevronDownIcon
                className={STYLES.FlightsToNearbyAlternatives__chevronDown}
              />
            </BpkButton>
          </BpkSectionListSection>
        </BpkSectionList>
      )}
    </BpkBox>
  );
};

const FlightsToNearbyAlternativesWithErrorBoundary = withErrorBoundary(
  FlightsToNearbyAlternatives,
  'copyright-component',
);

export default FlightsToNearbyAlternativesWithErrorBoundary;
